/**
 * Represents the status of a booking in the self-guided system.
 * This class provides predefined booking statuses and ensures that only one instance
 * of each status is created and reused.
 */
export default class BookingStatus {
  constructor(name) {
    this.name = name;
  }

  /**
   * Gets the instance representing the "Pending" status.
   * This status indicates that the booking is still in process.
   *
   * @returns {BookingStatus} An instance representing the "pending" status.
   */
  static get Pending() {
    return this.getInstance('pending');
  }

  /**
   * Gets the instance representing the "Scheduled" status.
   * This status indicates that the booking has been successfully scheduled.
   *
   * @returns {BookingStatus} An instance representing the "scheduled" status.
   */
  static get Scheduled() {
    return this.getInstance('scheduled');
  }

  /**
   * Gets the instance representing the "Failed" status.
   * This status indicates that the booking process has failed.
   *
   * @returns {BookingStatus} An instance representing the "failed" status.
   */
  static get Failed() {
    return this.getInstance('failed');
  }

  /**
   * Retrieves a `BookingStatus` instance based on the provided key.
   * Maps predefined keys to their corresponding `BookingStatus` instances.
   *
   * @param {string} key - The key representing a booking status.
   * @returns {BookingStatus} The corresponding `BookingStatus` instance.
   */
  static fromKey(key) {
    const statusMap = {
      pending: BookingStatus.Pending,
      scheduled: BookingStatus.Scheduled,
      failed: BookingStatus.Failed,
    };
    return statusMap[key];
  }

  /**
   * Retrieves or creates an instance of the `BookingStatus` class based on a given key.
   * Ensures that only one instance of the `BookingStatus` class is created for each unique key.
   *
   * @param {string} key - A unique identifier for the booking status.
   * @returns {BookingStatus} An instance of the `BookingStatus` class with the specified key.
   */
  static getInstance(key) {
    if (!this.instances[key]) {
      this.instances[key] = new BookingStatus(key);
    }
    return this.instances[key];
  }

  /**
   * Converts the `BookingStatus` instance to its string representation.
   *
   * @returns {string} The name of the booking status.
   */
  toString() {
    return `${this.name}`;
  }
}

Object.defineProperty(BookingStatus, 'instances', { value: {} });
